import { useMutation } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';

interface GitLabConnectFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export function GitLabConnectForm({ onSuccess, onCancel }: GitLabConnectFormProps): React.ReactElement {
  const orpc = useOrpc();
  const [label, setLabel] = useState('');
  const [baseUrl, setBaseUrl] = useState('https://gitlab.com');
  const [token, setToken] = useState('');

  const mutation = useMutation(
    orpc.settings.scm.addGitLabConnection.mutationOptions({
      onSuccess: () => {
        toast.success('GitLab connected');
        onSuccess();
      },
      onError: (err: Error) => toast.error(err.message),
    }),
  );

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    mutation.mutate({ label, baseUrl, token });
  };

  const canSubmit = !mutation.isPending && label.trim() !== '' && token.trim() !== '';

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Label</label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Work GitLab"
          className="w-full rounded border border-input bg-background px-2.5 py-1.5 text-sm"
          required
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Base URL</label>
        <input
          type="text"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://gitlab.com"
          className="w-full rounded border border-input bg-background px-2.5 py-1.5 text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Access Token</label>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="glpat-..."
          className="w-full rounded border border-input bg-background px-2.5 py-1.5 text-sm"
          required
        />
        <p className="mt-1 text-xs text-muted-foreground/60">
          Token must have the <code className="rounded bg-muted px-1 py-0.5 text-xs">api</code> scope and the token
          owner needs <strong>Maintainer</strong> or <strong>Owner</strong> role on any project you plan to link
          (webhook registration happens automatically on link).
        </p>
        <p className="mt-1 text-xs text-muted-foreground/60">
          For <strong>verified</strong> commits on GitLab.com or self-hosted 18.9+, enable web-commit signing on the
          target group (<em>Settings → Repository → Web commits</em>). Rudder AI commits via the REST API use the token
          owner&apos;s identity — commits show as verified when the token owner&apos;s GitLab email matches their
          profile.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center gap-1.5 rounded border border-brand bg-brand/10 px-2.5 py-1.5 text-sm text-brand transition-colors hover:bg-brand/20 disabled:opacity-30"
        >
          {mutation.isPending && <LoaderCircle size={14} className="animate-spin" />}
          Connect
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-2.5 py-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
